import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { withMinVisibleDuration } from "../lib/toolPrepCropApply";
import { useToolPrepDownloadFlow } from "./useToolPrepDownloadFlow";

export const TOOL_PREP_THANK_YOU_INITIAL = {
  open: false,
  progress: 0,
  phase: "downloading",
  shareCopied: false,
  errorMessage: "",
  imageUrl: null,
};

/**
 * Shared Apply → Result state for tool prep popups.
 * Download opens Options first, then Thank You progress/success.
 */
export function useToolPrepBakedApplyFlow({
  sourceFileName,
  fallbackDownloadName = "image.png",
  toolKey = "",
  shareTitle = "LazyStudio",
  shareText = "I edited my photo with LazyStudio!",
  thankYouCopy = {},
}) {
  const { getToken } = useAuth();
  const [applied, setApplied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resultUrl, setResultUrl] = useState(null);
  const [applyError, setApplyError] = useState(null);
  const resultUrlRef = useRef(null);
  const ephemeralPreviewRef = useRef(null);

  const downloadFlow = useToolPrepDownloadFlow({
    variant: "editing",
    sourceFileName,
    fallbackDownloadName,
    getToken,
    toolKey,
    shareTitle,
    shareText,
    thankYouCopy,
  });

  const revokeResultUrl = useCallback(() => {
    if (resultUrlRef.current) {
      URL.revokeObjectURL(resultUrlRef.current);
      resultUrlRef.current = null;
    }
    setResultUrl(null);
  }, []);

  const revokeEphemeralPreview = useCallback(() => {
    if (ephemeralPreviewRef.current) {
      URL.revokeObjectURL(ephemeralPreviewRef.current);
      ephemeralPreviewRef.current = null;
    }
  }, []);

  const resetApplied = useCallback(() => {
    setApplyError(null);
    setApplied(false);
    revokeResultUrl();
  }, [revokeResultUrl]);

  const apply = useCallback(
    async (exportFn) => {
      setApplyError(null);
      setBusy(true);
      try {
        const blob = await withMinVisibleDuration(exportFn());
        revokeResultUrl();
        const nextUrl = URL.createObjectURL(blob);
        resultUrlRef.current = nextUrl;
        setResultUrl(nextUrl);
        setApplied(true);
      } catch (err) {
        setApplyError(err?.message || "Could not apply.");
      } finally {
        setBusy(false);
      }
    },
    [revokeResultUrl]
  );

  const handleDownload = useCallback(
    async (exportFn) => {
      revokeEphemeralPreview();

      if (applied && resultUrlRef.current) {
        await downloadFlow.openDownloadOptions({
          previewUrl: resultUrlRef.current,
          resolveImageUrl: async () => resultUrlRef.current,
        });
        return;
      }

      if (typeof exportFn !== "function") return;

      try {
        const blob = await exportFn();
        const objectUrl = URL.createObjectURL(blob);
        ephemeralPreviewRef.current = objectUrl;
        await downloadFlow.openDownloadOptions({
          previewUrl: objectUrl,
          resolveImageUrl: async () => objectUrl,
        });
      } catch (err) {
        setApplyError(err?.message || "Could not prepare download.");
      }
    },
    [applied, downloadFlow, revokeEphemeralPreview]
  );

  useEffect(
    () => () => {
      revokeEphemeralPreview();
      if (resultUrlRef.current) {
        URL.revokeObjectURL(resultUrlRef.current);
        resultUrlRef.current = null;
      }
    },
    [revokeEphemeralPreview]
  );

  return {
    applied,
    busy,
    resultUrl,
    applyError,
    setApplyError,
    apply,
    resetApplied,
    revokeResultUrl,
    downloadBusy: downloadFlow.downloadBusy,
    handleDownload,
    thankYou: downloadFlow.thankYou,
    handleThankYouDismiss: downloadFlow.handleThankYouDismiss,
    handleShare: downloadFlow.handleShare,
    downloadFlow,
  };
}
